import {
  getAuthHeaders,
  getStoredAccessToken,
  resolveWebApiUrl,
  refreshAccessToken,
  readAuthErrorCode,
} from './webApi';
import { parseActivityLiveSseBlock } from './activityLiveSseParse.js';

const MAX_BACKOFF_MS = 30_000;
const INITIAL_BACKOFF_MS = 1_000;
const MAX_TOKEN_REFRESH_ATTEMPTS = 3;

/** Stop reconnecting when auth is expired or invalid (avoids 401 log storms). */
const AUTH_STOP = 'auth_stop';
const RETRY = 'retry';

/**
 * Subscribe to GET /api/activities/live (SSE) with reconnect/backoff.
 * Returns abort cleanup.
 */
export function subscribeActivityLiveStream({ onEvent, signal, enabled = true } = {}) {
  const controller = new AbortController();
  const abortSignal = signal ?? controller.signal;
  let closed = false;
  let lastEventId = '';
  let backoffMs = INITIAL_BACKOFF_MS;
  let reconnectTimer = null;
  let tokenRefreshAttempts = 0;

  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (closed || !enabled) return;
    clearReconnectTimer();
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void runOnce();
    }, backoffMs);
    backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
  };

  const openStream = async (res) => {
    tokenRefreshAttempts = 0;
    backoffMs = INITIAL_BACKOFF_MS;
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (!closed && enabled) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';
      for (const part of parts) {
        const lines = part.split('\n').map((line) => line.trim()).filter(Boolean);
        const parsed = parseActivityLiveSseBlock(lines);
        if (!parsed) continue;
        if (parsed.id) lastEventId = parsed.id;
        onEvent?.(parsed.payload);
      }
    }
  };

  const connectOnce = async (allowTokenRefresh, tokenOverride = null) => {
    if (!tokenOverride && !getStoredAccessToken()) {
      return AUTH_STOP;
    }

    const url = new URL(resolveWebApiUrl('/api/activities/live'));
    if (lastEventId) {
      url.searchParams.set('since', lastEventId);
    }

    const authHeaders = tokenOverride
      ? { 'x-auth-token': tokenOverride }
      : getAuthHeaders('GET');

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        ...authHeaders,
        ...(lastEventId ? { 'Last-Event-ID': lastEventId } : {}),
      },
      signal: abortSignal,
      credentials: 'include',
    });

    if (res.status === 401) {
      const code = await readAuthErrorCode(res);
      if (
        code === 'TOKEN_EXPIRED' &&
        allowTokenRefresh &&
        tokenRefreshAttempts < MAX_TOKEN_REFRESH_ATTEMPTS
      ) {
        tokenRefreshAttempts += 1;
        const newToken = await refreshAccessToken();
        if (newToken) {
          return connectOnce(false, newToken);
        }
      }
      return AUTH_STOP;
    }

    if (!res.ok || !res.body) {
      return RETRY;
    }

    await openStream(res);
    return RETRY;
  };

  const runOnce = async () => {
    if (closed || !enabled) return;

    try {
      const outcome = await connectOnce(true);
      if (outcome === AUTH_STOP) {
        return;
      }
    } catch {
      /* stream ended */
    }

    if (!closed && enabled) {
      scheduleReconnect();
    }
  };

  void runOnce();

  return () => {
    closed = true;
    clearReconnectTimer();
    controller.abort();
  };
}
